import { SmartSendProvider } from '~/lib/consts/providers';
import { QuoteState, SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { ExtendedOrderCache } from '../cache/ExtendedOrderCache';
import { SettingsCache } from '~/lib/consts/settings-cache';
import { KeyedSmartSendGroupCache } from '../cache/SmartSendGroupCache';
import { ISmartSendContext } from '../context/SmartSendContextModels';
import { ApplierOptionsForPotentialBulkActions } from './ApplierOption';
import { SumNumber, ToNumber, onlyUnique } from './ArrayHelper';
import ItemIsNotReady from './ItemIsNotReady';
import ItemIsReady from './ItemIsReady';
import GenerateOptions from './SmartSendBulkActionOptions';
import SmartSendFilterApplier, {
  DefaultSummary,
  SmartSendFilterContext,
  SmartSendSummary
} from './SmartSendFilterApplier';
import { ToInlineCommaList } from './StringHelper';
import { SmartSendItemGroup } from '../../components/smartsend/item/SmartSendGroup';
import { DefaultFilter } from './DefaultFilter';
import { SmartSendSubType } from '../consts/SmartSendSubType';

export function filterMasterItems(x: SmartSendItemApiModel) {
  return x.Status !== 'Deleted';
}

export function IsSmartSendByType(type: string) {
  return type === 'SmartSend';
}

export function IsSmartSend(x: SmartSendItemApiModel) {
  return IsSmartSendByType(x.Type);
}

export function SmartSendHasSource(x: SmartSendItemApiModel, source: string) {
  return x.Source.Id === source || x.MarketplaceOrderLines.find((m) => m.Source.Id === source);
}

export function CanMarkAsDispatched(x: SmartSendItemApiModel): boolean {
  return x.MarketplaceStatus == 'AwaitingDispatch' && HasExternalTrackingNumber(x);
}

export function HasExternalTrackingNumber(x: SmartSendItemApiModel): boolean {
  return !!GetExternalTrackingNumber(x);
}

export function GetExternalTrackingNumber(x: SmartSendItemApiModel): string {
  return x?.Meta['ExternalTrackingNumber']?.Value;
}

export function HasClaims(item: SmartSendItemApiModel) {
  return !!ExtendedOrderCache.claimOrderLineIds.find((x) => x.orderlineId === item.BookingDetails?.OrderLineId);
}

export function IsInternational(item: SmartSendItemApiModel) {
  return item.Recipient?.CountryIsoCode != item?.Sender?.CountryIsoCode;
}

export function IsSavedForLater(item: SmartSendItemApiModel) {
  return item.MarketplaceStatus === 'Sold' && item.Status == 'Inactive';
}

export function IsDomestic(item: SmartSendItemApiModel) {
  return item.Recipient?.CountryIsoCode === item.Sender?.CountryIsoCode;
}

export function IsSelected(item: SmartSendItemApiModel) {
  return item.State?.selected === true;
}

export function HasRefund(item: SmartSendItemApiModel) {
  return !!ExtendedOrderCache.refundOrderLineIds.find((x) => x.orderlineId === item.BookingDetails?.OrderLineId);
}

export function CanGenerateLabel(x: SmartSendItemApiModel) {
  return (
    SettingsCache.settings.Features.includes('Labels') &&
    x.BookingDetails?.Service?.Provider === SmartSendProvider &&
    x.BookingDetails?.OrderLineId
  );
}

export function IsReturnCollection(item: SmartSendItemApiModel) {
  return item.Tags?.includes('ReturnCollection');
}

export function ItemShouldAllowCustomerUpdate(item: SmartSendItemApiModel) {
  return item.Status == 'Active' && item.MarketplaceStatus == 'Sold' && !IsReturnCollection(item);
}

const deliveryTypes = ['Shop', 'Locker'];
export const isDeliveryToShopService = (item: SmartSendItemApiModel) => {
  return (
    item.State?.validation?.QuoteState?.Quote?.Service?.PickUpProviderCode &&
    deliveryTypes.includes(item.State.validation.QuoteState.Quote.Service.DeliveryType)
  );
};

export function GetQuoteServiceTags(x: QuoteState): string[] {
  return x?.Quote?.Service?.Tags || [];
}

export function ServiceSupportsMultiParcelShipment(x: QuoteState): boolean {
  return GetQuoteServiceTags(x).includes('MultiConsignment');
}

export function ItemServiceSupportsMultiParcelShipment(x: SmartSendItemApiModel): boolean {
  return ServiceSupportsMultiParcelShipment(x.State?.validation?.QuoteState);
}

export function GetItemServiceTags(x: SmartSendItemApiModel): string[] {
  return GetQuoteServiceTags(x.State?.validation?.QuoteState);
}

export function GetItemWeight(x: SmartSendItemApiModel) {
  return Number(
    (x.Consignment?.Parcels?.length > 0
      ? SumNumber(x.Consignment.Parcels.map((p) => p.Dimensions.Weight))
      : ToNumber(x.PackageDimensions.Weight)
    ).toFixed(2)
  );
}

export function IsAwaitingDispatch(x: SmartSendItemApiModel) {
  return (
    IsSmartSend(x) &&
    x.Status === 'Active' &&
    x.MarketplaceStatus === 'AwaitingDispatch' &&
    !x.TrackingSummary?.DroppedOff &&
    !x.TrackingSummary?.Collected
  );
}

export function ToItemSource(x: { Source: { Name: string }; Type?: string }) {
  if (IsSmartSendByType(x.Type)) {
    return x.Source.Name;
  }
  switch (x.Type) {
    case 'BulkUpload':
    case 'AutoSend':
      return 'Legacy Smart Send';
    case 'OrderProcess':
    case 'OrderProcess:V2.0':
    case 'Reorder':
      return 'Order Process';
    default:
      return 'Non-Smart Send';
  }
}

export const ToItemSources = (x: SmartSendItemApiModel) =>
  ToInlineCommaList(x.MarketplaceOrderLines.map((m) => m.Source.Name).filter(onlyUnique));

export default function SmartSendSummaryGenerator(
  state: ISmartSendContext,
  soldItemsFilter: SmartSendFilterContext,
  resetSelection: boolean,
  smartSendItemsCache: SmartSendItemApiModel[]
): ISmartSendContext {
  for (const key in state.filters) {
    const res = SmartSendSummaryGeneratorActual(
      key,
      resetSelection,
      smartSendItemsCache,
      key === SmartSendSubType ? soldItemsFilter : state.filters[key].filter
    );
    state.filters[key].summary = res.summary;
    state.filters[key].filter = res.filter;
    KeyedSmartSendGroupCache[key] = res.groups;
  }

  return state;
}

export function SmartSendSummaryGeneratorActual(
  key: string,
  resetSelection: boolean,
  smartSendItemsCache: SmartSendItemApiModel[],
  filter?: SmartSendFilterContext
): { summary: SmartSendSummary; filter: SmartSendFilterContext; groups: SmartSendItemGroup[] } {
  const newFilter: SmartSendFilterContext = filter ? { ...filter } : { ...DefaultFilter() };

  const items = smartSendItemsCache.filter(filterMasterItems);
  const groups = SmartSendFilterApplier(items, newFilter);

  const newSummary: SmartSendSummary = { ...DefaultSummary() };
  newSummary.count = 0;
  newSummary.ready = 0;
  newSummary.notReady = 0;
  newSummary.savedForLater = 0;
  newSummary.selected = [];
  newSummary.groupCount = groups.length;
  newSummary.bulkOptions = [];
  newSummary.potentialBulkOptions = [];
  newSummary.states = [];

  const baseStats = ['Sold', 'Ready', 'SavedForLater', 'NotReady', 'Groupable'];
  baseStats.forEach((s) => {
    if (!newSummary.stats.find((b) => b.key == s)) {
      newSummary.stats.push({ key: s, value: 0 });
    }
  });

  newSummary.stats.forEach((s) => {
    switch (s.key) {
      case 'Sold':
        s.value = items.filter(IsSmartSend).filter((x) => x.MarketplaceStatus === 'Sold').length;
        break;
      case 'Ready':
        s.value = items.filter(IsSmartSend).filter(ItemIsReady).length;
        break;
      case 'SavedForLater':
        s.value = items.filter(IsSmartSend).filter(IsSavedForLater).length;
        break;
      case 'NotReady':
        s.value = items.filter(IsSmartSend).filter(ItemIsNotReady).length;
        break;
      case 'Groupable':
        {
          let cnt = 0;
          const keyStore = {};
          items
            .filter((i) => IsSmartSend(i) && i.Status === 'Active' && i.MarketplaceStatus === 'Sold')
            .map((i) => i.PropertyKey)
            .forEach((x) => {
              if (keyStore[x]) {
                cnt++;
              } else {
                keyStore[x] = true;
              }
            });
          s.value = cnt;
        }
        break;
    }
  });

  groups.forEach((g) => {
    g.Items.forEach((i) => {
      const itemOptions = GenerateOptions(i, i.State?.validation);
      if (resetSelection) {
        i.State.selected = false;
      } else {
        newSummary.count++;
        if (IsSelected(i)) {
          newSummary.selected.push(i.Id);
          newSummary.bulkOptions = newSummary.bulkOptions.concat(itemOptions);
        }
      }

      newSummary.potentialBulkOptions = newSummary.potentialBulkOptions
        .concat(itemOptions)
        .filter((o) => ApplierOptionsForPotentialBulkActions.includes(o));

      if (ItemIsReady(i)) {
        newSummary.ready++;
      }
      if (ItemIsNotReady(i)) {
        newSummary.notReady++;
      }
      if (IsSavedForLater(i)) {
        newSummary.savedForLater++;
      }
      if (IsAwaitingDispatch(i)) {
        newSummary.awaitingDispatch++;
      }
    });
  });

  groups.forEach((g) => {
    newSummary.states = newSummary.states.concat(g.States).filter(onlyUnique);
  });

  let indexToShowFrom = newFilter.CurrentFilter.Skip;
  if (indexToShowFrom > newSummary.count) {
    // If they are filtering and the paging is greater than that which can be returned, reset.
    newFilter.CurrentFilter.Skip = 0;
    indexToShowFrom = 0;
  }
  const indexToShowTo = indexToShowFrom + newFilter.CurrentFilter.Take;

  const hidden = [];

  if (key === SmartSendSubType) {
    // Show and Hide based on item index
    // SmartSend Is Special as its the only one which holds the full list of items and does paging client side
    let index = 0;
    groups.forEach((g) => {
      const hiddenResults: boolean[] = [];
      g.Items.forEach((i) => {
        const itemIsHidden = index < indexToShowFrom || index >= indexToShowTo;
        if (itemIsHidden) {
          hidden.push(i.Id);
          hiddenResults.push(itemIsHidden);
        }
        index++;
      });
      g.Hidden = g.Items.length === hiddenResults.length;
    });
  }

  const visibleGroups = groups
    .filter((x) => !x.Hidden)
    .map((x) => {
      return { ...x, Items: x.Items.filter((i) => !hidden.includes(i.Id)) };
    });

  return { summary: newSummary, filter: newFilter, groups: visibleGroups };
}
